import * as PusherTypes from 'pusher-js';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import {
  type ChannelsTable,
  getPusherConnectionSnapshot,
  pusherStore,
  type SubscribeCallbacks,
  subscribePusherConnection,
} from './pusherStore';

interface PusherContextDefinition {
  getPusher: () => PusherTypes.default;
  subscribe: (
    pusher: PusherTypes.default,
    channelName: string,
    callbacks: SubscribeCallbacks,
    joinType?: string,
  ) => PusherTypes.Channel;
  getChannels: (pusher: PusherTypes.default) => ChannelsTable;
  getPresenceChannelMembers: (
    pusher: PusherTypes.default,
    channelName: string,
    shouldUnsubscribeAfter: boolean,
    joinType?: string,
  ) => Promise<PusherTypes.Members>;
  hasConnectionError: boolean;
}

const pusherNotInitialized = () => {
  throw new Error('Pusher not initialized');
};

const PusherContext = createContext<PusherContextDefinition>({
  getPusher: pusherNotInitialized,
  subscribe: pusherNotInitialized,
  getChannels: pusherNotInitialized,
  getPresenceChannelMembers: pusherNotInitialized,
  hasConnectionError: false,
});

export const PusherProvider = ({ children }: { children: React.ReactNode }) => {
  const hasConnectionError = useSyncExternalStore(
    subscribePusherConnection,
    getPusherConnectionSnapshot,
  );

  const value = useMemo<PusherContextDefinition>(
    () => ({
      getPusher: pusherStore.getPusher,
      subscribe: pusherStore.subscribe,
      getChannels: pusherStore.getChannels,
      getPresenceChannelMembers: pusherStore.getPresenceChannelMembers,
      hasConnectionError,
    }),
    [hasConnectionError],
  );

  return (
    <PusherContext.Provider value={value}>{children}</PusherContext.Provider>
  );
};

export const usePusherContext = () => {
  return useContext(PusherContext);
};

type UsePusherChannelOptions = {
  joinType?: string;
  onSubscriptionSucceeded?: SubscribeCallbacks['success'];
  onSubscriptionError?: NonNullable<SubscribeCallbacks['error']>;
};

export function usePusherChannel(
  channelName: string | null | undefined,
  options?: UsePusherChannelOptions,
) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const joinType = options?.joinType;

  const [channel, setChannel] = useState<
    PusherTypes.Channel | PusherTypes.PresenceChannel | null
  >(null);

  useEffect(() => {
    if (!channelName) {
      setChannel(null);
      return;
    }
    const pusher = pusherStore.getPusher();
    const nextChannel = pusherStore.subscribe(
      pusher,
      channelName,
      {
        success: (data) => optionsRef.current?.onSubscriptionSucceeded?.(data),
        error: (err) => optionsRef.current?.onSubscriptionError?.(err),
      },
      joinType,
    );
    setChannel(nextChannel);
    return () => {
      pusher.unsubscribe(channelName);
      setChannel(null);
    };
  }, [channelName, joinType]);

  return { channel };
}
